// Plan 23a Task 3.1 — Panel primitive. Drives gsapRegistry directly (not Plan 20a modal-menu-swirl)
// because the hook hardcodes scale 0.85 and Modal needs two coordinated tweens.

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface PanelOpts {
  /** Padding preset. Default: 'standard'. */
  padding?: 'standard' | 'lg';
}

export interface PanelHandle extends UIComponentHandle {
  show(): void;
  hide(): void;
  setContent(node: HTMLElement): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** animation.duration.normal = 300ms */
const SHOW_DURATION_S = 0.3;

/** animation.duration.fast = 150ms */
const HIDE_DURATION_S = 0.15;

/** animation.easing.bouncy from tokens.json */
const BOUNCY_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** animation.easing.smooth from tokens.json */
const SMOOTH_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createPanel(config: UIComponentConfig & PanelOpts): PanelHandle {
  const { gsapRegistry, mount, padding = 'standard' } = config;

  // Resolved padding values — tokens.json layout.panel-padding / layout.panel-padding-lg
  const paddingValue = padding === 'lg' ? '24px 28px' : '16px 20px';

  // ── Create DOM ────────────────────────────────────────────────────────────

  const el = document.createElement('div');
  el.className = 'ui-panel';

  el.style.cssText = [
    // color.ui.panel-bg-alpha
    `background: var(--color-ui-panel-bg-alpha, rgba(30, 35, 50, 0.82))`,
    // border.panel
    `border: var(--border-panel, 2px solid rgba(255, 255, 255, 0.10))`,
    // border-radius.lg
    `border-radius: var(--border-radius-lg, 12px)`,
    // shadow.panel
    `box-shadow: var(--shadow-panel, 0 4px 12px rgba(0, 0, 0, 0.50))`,
    // NO backdrop-filter (per spec: no blur, no glassmorphism)
    `padding: ${paddingValue}`,
    `box-sizing: border-box`,
  ].join('; ');

  let disposed = false;

  /** Latest timeline handle — show or hide. dispose() kills it. */
  let currentHandle: GsapTimelineHandle | null = null;

  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  // ── Mount helper ────────────────────────────────────────────────────────

  function appendToMount(): void {
    const target = mount;
    if (!target) return;
    if (!target.contains(el)) {
      target.appendChild(el);
    }
  }

  function removeFromParent(): void {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  // ── show ─────────────────────────────────────────────────────────────────

  function show(): void {
    if (disposed) return;

    // Kill any in-flight animation (e.g. hide was in progress)
    killCurrent();

    // Mount the element if not already in the DOM
    appendToMount();

    // Entrance: scale(0.92) translateY(8px) opacity:0 → scale(1) translateY(0) opacity:1
    const handle = gsapRegistry.create('panel:show');
    const tl = handle.timeline;

    tl.fromTo(
      el,
      { scale: 0.92, y: 8, opacity: 0 },
      {
        scale: 1,
        y: 0,
        opacity: 1,
        duration: SHOW_DURATION_S,
        ease: BOUNCY_EASE,
      },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );

    currentHandle = handle;
  }

  // ── hide ─────────────────────────────────────────────────────────────────

  function hide(): void {
    if (disposed) return;
    if (!el.parentNode) return; // not mounted, nothing to hide

    // Kill any in-flight show animation
    killCurrent();

    // Exit: scale(1) opacity:1 → scale(0.95) opacity:0
    const handle = gsapRegistry.create('panel:hide');
    const tl = handle.timeline;

    tl.to(
      el,
      {
        scale: 0.95,
        opacity: 0,
        duration: HIDE_DURATION_S,
        ease: SMOOTH_EASE,
      },
      0, // explicit position arg — sentinel-safe (F20a-CLASS-1)
    );

    // On natural completion: detach from DOM.
    // tl.call(fn, undefined, absoluteSeconds) — NOT eventCallback('onComplete').
    // eventCallback fires at VIRTUAL_DURATION (3600s) due to the sentinel tween.
    tl.call(
      removeFromParent,
      undefined,
      HIDE_DURATION_S, // fire at the natural end of the hide animation
    );

    currentHandle = handle;
  }

  // ── setContent ────────────────────────────────────────────────────────────

  function setContent(node: HTMLElement): void {
    if (disposed) return;
    // Clear all existing children
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
    el.appendChild(node);
  }

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill any in-flight timeline
    killCurrent();

    // Detach from DOM
    removeFromParent();
  }

  // ── Initial mount (if mount target provided at construction time) ─────────
  // Do NOT auto-show here — mount target is optional at construct time.
  // Callers call show() to animate the panel in.
  // (Element is NOT appended at construct time; only on show().)

  return { element: el, show, hide, setContent, dispose };
}
